import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBook,
  FaCreditCard,
  FaEye,
  FaGraduationCap,
} from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";
import InstallmentPaymentModal from "./InstallmentPaymentModal";
import { formatBDT } from "../Courses/types/formatCurrency";

interface Installment {
  id: number;
  sequenceNumber: number;
  dueDate: string;
  amount: number;
  paid: boolean;
  paidDate?: string;
  status: "PAID" | "UPCOMING" | "UNPAID" | "IN_REVIEW";
  inReview: boolean;
  reviewRequestDate?: string;
}

interface Course {
  id: number;
  title: string;
  price: number;
  status: string;
  installments: Installment[];
}

const StudentCourses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInstallmentId, setSelectedInstallmentId] = useState<
    number | null
  >(null);
  const { student } = useAuth();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/enrollments/my-courses`,
        {
          credentials: "include",
        }
      );
      const data = await res.json();
      setCourses(data);
    } catch (err) {
      console.error("Failed to fetch student courses", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayClick = (installmentId: number) => {
    setSelectedInstallmentId(installmentId);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    fetchCourses(); // Refresh courses to update installment status
  };

  // Helper function to check if installment can be paid
  const canPayInstallment = (installment: Installment) => {
    if (installment.paid) return false;
    if (installment.inReview) return false;
    const today = new Date();
    const dueDate = new Date(installment.dueDate);
    return today <= dueDate;
  };

  // Helper function to get pay button
  const getPayButton = (installment: Installment) => {
    // Normalize inReview to boolean (handles string/boolean inputs)
    const inReview =
      installment.inReview === true || installment.status === "IN_REVIEW";

    if (installment.paid || inReview) {
      // Show disabled "Under Review" if in review, or nothing if paid
      return inReview ? (
        <button
          disabled
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200"
          title="Payment is under review"
          type="button"
        >
          <FaCreditCard className="text-xs" />
          Pay
        </button>
      ) : null;
    }

    // Allow payment only if not past due
    const today = new Date();
    const dueDate = new Date(installment.dueDate);
    const canPay = today <= dueDate;

    return (
      <button
        onClick={() => handlePayClick(installment.id)}
        disabled={!canPay}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shadow-sm ${
          canPay
            ? "bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-md"
            : "bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200"
        }`}
        title={canPay ? "Pay this installment" : "Payment deadline has passed"}
        type="button"
      >
        <FaCreditCard className="text-xs" />
        Pay
      </button>
    );
  };

  // Helper function to get status display for student view
  const getInstallmentStatusDisplay = (installment: Installment) => {
    switch (installment.status) {
      case "PAID":
        return (
          <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full text-xs font-semibold border border-emerald-200">
            <FaCheckCircle className="text-emerald-500" />
            Paid
            {installment.paidDate
              ? ` (${new Date(installment.paidDate).toLocaleDateString()})`
              : ""}
          </span>
        );
      case "IN_REVIEW":
        return (
          <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-3 py-1 rounded-full text-xs font-semibold border border-amber-200">
            <FaEye className="text-amber-500" />
            Under Review
            {installment.reviewRequestDate
              ? ` (${new Date(
                  installment.reviewRequestDate
                ).toLocaleDateString()})`
              : ""}
          </span>
        );
      case "UPCOMING":
        return (
          <span className="inline-flex items-center gap-1.5 text-blue-700 bg-blue-50 px-3 py-1 rounded-full text-xs font-semibold border border-blue-200">
            <FaClock className="text-blue-500" />
            Upcoming
          </span>
        );
      case "UNPAID":
        return (
          <span className="inline-flex items-center gap-1.5 text-rose-600 bg-rose-50 px-3 py-1 rounded-full text-xs font-semibold border border-rose-200">
            <FaTimesCircle className="text-rose-500" />
            Unpaid
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 text-gray-600 bg-gray-50 px-3 py-1 rounded-full text-xs font-semibold border border-gray-200">
            <FaTimesCircle className="text-gray-500" />
            Unknown
          </span>
        );
    }
  };

  // Split courses with installments and without
  const coursesWithInstallments = courses.filter(
    (c) => c.installments && c.installments.length > 1
  );
  const coursesWithoutInstallments = courses.filter(
    (c) => !c.installments || c.installments.length <= 1
  );

  // Helper for status badge
  function statusBadge(status: string) {
    const statusMap: Record<string, string> = {
      ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
      PENDING: "bg-amber-50 text-amber-700 border-amber-200",
      COMPLETED: "bg-blue-50 text-blue-700 border-blue-200",
      CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold uppercase border ${
          statusMap[status] || "bg-gray-50 text-gray-700 border-gray-200"
        }`}
      >
        {status}
      </span>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaCreditCard />}
        title="My Courses"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "My Courses" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome Section */}
        <div className="mb-10 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-xl p-6 shadow-lg">
          <h2 className="text-3xl font-bold mb-2 text-white flex items-center gap-2">
            <FaGraduationCap />
            {student?.firstName
              ? `Welcome, ${student.firstName} 👋`
              : "Welcome 👋"}
          </h2>
          <p className="text-white opacity-90 text-base">
            Here you can see all your enrolled courses and payment status at a
            glance.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <FaBook className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No Courses Found
            </h3>
            <p className="text-gray-500">
              You are not enrolled in any courses yet.
            </p>
          </div>
        ) : (
          <>
            {/* Full-width cards for courses with installments */}
            {coursesWithInstallments.map((course) => (
              <div
                key={course.id}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden mb-8 w-full"
              >
                {/* Course Header */}
                <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <FaBook /> {course.title}
                      </h3>
                      <p className="text-cyan-100 text-sm mt-1">
                        Total Price: {formatBDT(course.price)}
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                      {statusBadge(course.status)}
                    </div>
                  </div>
                </div>

                {/* Course Content */}
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FaCreditCard className="text-[#009fe3]" />
                    Payment Installments
                  </h4>

                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-sm overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                            #
                          </th>
                          <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                            Due Date
                          </th>
                          <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                            Amount
                          </th>
                          <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                            Status
                          </th>
                          <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {course.installments.map((inst, index) => (
                          <tr
                            key={inst.id}
                            className={`border-b border-gray-100 ${
                              index % 2 === 1 ? "bg-gray-50" : "bg-white"
                            } hover:bg-blue-50 transition-colors`}
                          >
                            <td className="py-3 px-4 font-semibold text-gray-800">
                              {inst.sequenceNumber}
                            </td>
                            <td className="py-3 px-4 text-gray-700">
                              <div className="flex items-center gap-1">
                                <FaClock className="text-xs text-gray-400" />
                                {new Date(inst.dueDate).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="py-3 px-4 font-medium text-gray-800">
                              {formatBDT(inst.amount)}
                            </td>
                            <td className="py-3 px-4">
                              {getInstallmentStatusDisplay(inst)}
                            </td>
                            <td className="py-3 px-4">{getPayButton(inst)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}

            {/* Grid for other courses */}
            {coursesWithoutInstallments.length > 0 && (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {coursesWithoutInstallments.map((course) => (
                  <div
                    key={course.id}
                    className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden flex flex-col"
                  >
                    {/* Course Header */}
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold flex items-center gap-2">
                          <FaBook /> {course.title}
                        </h3>
                        {statusBadge(course.status)}
                      </div>
                      <p className="text-emerald-100 text-sm mt-1">
                        Price: {formatBDT(course.price)}
                      </p>
                    </div>

                    {/* Course Content */}
                    <div className="p-4 flex-1">
                      <h4 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <FaCreditCard className="text-emerald-600" />
                        Installments
                      </h4>
                      {course.installments.length === 0 ? (
                        <div className="text-center py-4">
                          <FaCreditCard className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                          <p className="text-sm text-gray-500">
                            No installments found.
                          </p>
                        </div>
                      ) : (
                        <ul className="space-y-3">
                          {course.installments.map((inst) => (
                            <li
                              key={inst.id}
                              className="bg-white rounded-lg p-3 border border-emerald-100 shadow-sm"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded text-xs">
                                    #{inst.sequenceNumber}
                                  </span>
                                  <span className="text-gray-600 text-xs flex items-center gap-1">
                                    <FaClock />
                                    {new Date(
                                      inst.dueDate
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                                <span className="font-semibold text-gray-800">
                                  {formatBDT(inst.amount)} BDT
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                {getInstallmentStatusDisplay(inst)}
                                <div className="flex-shrink-0">
                                  {getPayButton(inst) || (
                                    <span className="text-gray-400 text-xs">
                                      -
                                    </span>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Payment Modal */}
      {selectedInstallmentId && (
        <InstallmentPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedInstallmentId(null);
          }}
          installmentId={selectedInstallmentId}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default StudentCourses;
